import DeleteIcon from '@mui/icons-material/Delete'
import { IconButton, TableCell, TableRow } from '@mui/material'
import { capitalCase } from 'change-case'
import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useStickyHeaderStyles } from 'components/Table'
import { MIN_ESTIMATED_STICKY_ROWS } from 'components/Table/Table/components/useStickyHeaderStyles'
import {
  AbTestsAddEditContext,
  AbTestsAddEditContextType,
} from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { AbTestsCommonSchema, Variants } from '../../abTestsCommonSchema'
import { rearrangeTrafficShare, reassignDefaultReportedKeyValues } from '../../utils'
import { HorizontalHeaderRowProps } from './types'

export const HorizontalHeaderRow = ({
  data,
  sx,
  verticalHeaderLength,
}: HorizontalHeaderRowProps): JSX.Element => {
  const { t } = useTranslation('features/abTests', { keyPrefix: 'common.variants.table' })
  const { isDisabled } = useContext<AbTestsAddEditContextType>(AbTestsAddEditContext)
  const { setValue, watch } = useFormContext<AbTestsCommonSchema>()
  const { variants } = watch()

  const showDeleteIcon = variants && Object.keys(variants).length > 2

  const handleDeleteVariant = (variants: Variants, header: string): void => {
    if (header in variants) {
      delete variants[header as keyof Variants]

      setValue('variants', rearrangeTrafficShare(reassignDefaultReportedKeyValues(variants)), {
        shouldDirty: true,
      })
    }
  }

  const tableHeadStickyStyles = useStickyHeaderStyles({ variant: 'tableHead' })

  return (
    <TableRow sx={data.rows.length > MIN_ESTIMATED_STICKY_ROWS ? tableHeadStickyStyles : {}}>
      <TableCell
        colSpan={verticalHeaderLength}
        sx={sx}
      >
        {t('configuration')}
      </TableCell>

      {data.horizontalHeaders.map(
        (header: string, index: number): JSX.Element => (
          <TableCell
            key={index}
            sx={sx}
          >
            {capitalCase(header)}

            {showDeleteIcon && (
              <IconButton
                disabled={isDisabled}
                onClick={(): void =>
                  variants ? void handleDeleteVariant(variants, header) : undefined
                }
                sx={{ mx: 2, p: 0 }}
              >
                <DeleteIcon data-cy='delete-icon' />
              </IconButton>
            )}
          </TableCell>
        ),
      )}
    </TableRow>
  )
}
